/** Asset detail: attributes, component tree, meters, certificates, open work orders. */
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { DocumentFileLink } from "@/features/entities/DocumentFileLink";
import {
  CertificateDialog,
  InstallDialog,
  PositionDialog,
  RemoveDialog,
  RevokeCertificateDialog,
} from "@/features/assets/AssetActionDialogs";
import { AssetFormDialog } from "@/features/assets/AssetFormDialog";
import { AssetMeterReading } from "@/features/assets/AssetMeterReading";
import { AssetMeterHistory } from "@/features/assets/AssetMeterHistory";
import { AssetMaintenanceHistory } from "@/features/assets/AssetMaintenanceHistory";
import { ComponentTree } from "@/features/assets/AssetSummaryCard";
import { Attachments } from "@/features/documents/Attachments";
import {
  ASSET_STATUSES,
  type Certificate,
  useAsset,
  useAssetCertificates,
  useAssetTree,
  useComponentHealth,
  useAssetWorkOrders,
  useChangeAssetStatus,
} from "@/features/assets/queries";
import {
  Details,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "@/shared/components/page";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatPoint,
  formatQuantity,
  formatRelative,
} from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import { Field, Select, Textarea } from "@/shared/ui/form";
import {
  Dialog,
  Table,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Td,
  Th,
  Tr,
} from "@/shared/ui/primitives";

/** Dialog to change the asset's operational status. */
function StatusDialog({
  assetId,
  current,
  open,
  onOpenChange,
}: {
  assetId: string;
  current: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [status, setStatus] = useState(current);
  const [reason, setReason] = useState("");
  const mutation = useChangeAssetStatus(assetId);

  const submit = (e: { preventDefault(): void }): void => {
    e.preventDefault();
    mutation.mutate({ status, reason }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t("assets.changeStatus")}>
      <form id="status-form" onSubmit={submit} className="space-y-4">
        <Field id="new-status" label={t("app.status")}>
          <Select id="new-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {ASSET_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`assets.status.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="status-reason" label={t("app.reason")}>
          <Textarea id="status-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {mutation.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(mutation.error)}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("app.cancel")}
          </Button>
          <Button type="submit" disabled={mutation.isPending || status === current}>
            {t("app.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Asset detail page. */
export function AssetDetailPage(): ReactElement {
  const { t, i18n } = useTranslation();
  const { assetId = "" } = useParams({ strict: false });
  const { tab } = useSearch({ strict: false });
  const { can } = useAuth();
  const asset = useAsset(assetId);
  const tree = useAssetTree(assetId);
  const health = useComponentHealth(assetId);
  const certs = useAssetCertificates(assetId, can("assets.certificate.view"));
  const wos = useAssetWorkOrders(
    assetId,
    can("workorders.workorder.view_assigned") || can("workorders.workorder.view_all"),
  );
  const [statusOpen, setStatusOpen] = useState(false);
  const [dialog, setDialog] = useState<
    | { kind: "edit" }
    | { kind: "component" }
    | { kind: "certificate"; certificate?: Certificate }
    | { kind: "revoke"; certificate: Certificate }
    | { kind: "install" }
    | { kind: "remove" }
    | { kind: "position" }
    | { kind: "reading" }
    | null
  >(null);

  if (asset.isPending) {
    return <LoadingState />;
  }
  if (asset.isError) {
    return <ErrorState error={asset.error} onRetry={() => void asset.refetch()} />;
  }
  const a = asset.data;

  return (
    <div>
      <PageHeader
        title={`${a.code} · ${a.name}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {a.type.name}
            <StatusBadge value={a.status} namespace="assets.status" />
            <StatusBadge value={a.criticality} namespace="assets.criticalityLevel" />
          </span>
        }
        actions={
          <>
            {can("assets.asset.edit") ? (
              <Button variant="outline" onClick={() => setDialog({ kind: "edit" })}>
                {t("app.edit")}
              </Button>
            ) : null}
            {can("assets.asset.change_status") ? (
              <Button variant="outline" onClick={() => setStatusOpen(true)}>
                {t("assets.changeStatus")}
              </Button>
            ) : null}
            {can("assets.position.record") ? (
              <Button variant="outline" onClick={() => setDialog({ kind: "position" })}>
                {t("assets.recordPosition")}
              </Button>
            ) : null}
            {can("assets.asset.install") ? (
              a.parent_id ? (
                <Button variant="outline" onClick={() => setDialog({ kind: "remove" })}>
                  {t("assets.remove")}
                </Button>
              ) : a.can_install ? (
                <Button variant="outline" onClick={() => setDialog({ kind: "install" })}>
                  {t("assets.install")}
                </Button>
              ) : null
            ) : null}
            {can("requests.request.create") ? (
              <Button asChild variant="secondary">
                <Link to="/requests" search={{ asset_id: a.id }}>
                  {t("requests.new")}
                </Link>
              </Button>
            ) : null}
            {can("workorders.workorder.create") ? (
              <Button asChild>
                <Link to="/work-orders" search={{ asset_id: a.id }}>
                  {t("workOrders.new")}
                </Link>
              </Button>
            ) : null}
          </>
        }
      />
      {statusOpen ? (
        <StatusDialog
          assetId={a.id}
          current={a.status}
          open={statusOpen}
          onOpenChange={setStatusOpen}
        />
      ) : null}
      {dialog?.kind === "edit" ? (
        <AssetFormDialog open onOpenChange={() => setDialog(null)} asset={a} />
      ) : null}
      {dialog?.kind === "component" ? (
        <AssetFormDialog
          open
          onOpenChange={() => setDialog(null)}
          parentTypeId={a.type.id}
          defaults={{
            parent_id: a.id,
            site_id: a.site?.id ?? "",
            location_id: a.location?.id ?? "",
          }}
        />
      ) : null}
      {dialog?.kind === "certificate" ? (
        <CertificateDialog
          asset={a}
          certificate={dialog.certificate}
          open
          onOpenChange={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "revoke" ? (
        <RevokeCertificateDialog certificate={dialog.certificate} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "install" ? (
        <InstallDialog asset={a} open onOpenChange={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "remove" ? (
        <RemoveDialog asset={a} open onOpenChange={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "position" ? (
        <PositionDialog asset={a} open onOpenChange={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "reading" ? (
        <AssetMeterReading asset={a} onClose={() => setDialog(null)} />
      ) : null}
      <Details
        items={[
          { label: t("assets.serial"), value: a.serial_number || "—" },
          { label: t("assets.model"), value: a.model?.name ?? "—" },
          { label: t("assets.manufacturer"), value: a.manufacturer?.name ?? "—" },
          { label: t("assets.owner"), value: a.owner?.name ?? t("assets.tenantOwned") },
          { label: t("assets.operatingUnit"), value: a.operating_unit?.name ?? "—" },
          { label: t("assets.maintenanceTeam"), value: a.maintenance_team?.name ?? "—" },
          { label: t("assets.costCenter"), value: a.cost_center?.name ?? "—" },
          {
            label: t("assets.parent"),
            value: a.parent ? (
              <AssetReference id={a.parent.id} code={a.parent.code} name={a.parent.name} />
            ) : a.parent_id ? (
              t("assets.summary.unavailableParent")
            ) : (
              "—"
            ),
          },
          { label: t("app.site"), value: a.site?.name ?? "—" },
          { label: t("assets.location"), value: a.location?.name ?? "—" },
          { label: t("assets.commissioned"), value: formatDate(a.commissioned_on) },
          { label: t("assets.warrantyUntil"), value: formatDate(a.warranty_until) },
          { label: t("assets.purchaseCost"), value: formatMoney(a.purchase_cost, i18n.language) },
          {
            label: t("assets.position"),
            value: a.current_position ? (
              <Link to="/map" className="hover:underline">
                {formatPoint(a.current_position)} · {formatRelative(a.current_position_at)}
              </Link>
            ) : (
              t("assets.noPosition")
            ),
          },
          { label: t("app.description"), value: a.description || "—" },
        ]}
      />

      <Tabs
        key={`${a.id}-${tab ?? "components"}`}
        defaultValue={tab ?? "components"}
        className="mt-6"
      >
        <TabsList>
          <TabsTrigger value="components">
            {t("assets.children")} ({a.child_count ?? 0})
          </TabsTrigger>
          <TabsTrigger value="meters">
            {t("assets.meters")} ({a.meters?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="workorders">
            {t("assets.openWorkOrders")} ({wos.data?.length ?? 0})
          </TabsTrigger>
          {can("workorders.workorder.view_assigned") || can("workorders.workorder.view_all") ? (
            <TabsTrigger value="history">{t("assets.maintenanceHistory")}</TabsTrigger>
          ) : null}
          <TabsTrigger value="documents">{t("documents.title")}</TabsTrigger>
          <TabsTrigger value="certificates">
            {t("assets.certificates")} ({certs.data?.length ?? 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="components">
          {can("assets.asset.create") && a.can_host_components ? (
            <div className="mb-2 flex justify-end">
              <Button size="sm" variant="outline" onClick={() => setDialog({ kind: "component" })}>
                {t("assets.addComponent")}
              </Button>
            </div>
          ) : null}
          {tree.isPending ? (
            <LoadingState />
          ) : tree.isError ? (
            <ErrorState error={tree.error} />
          ) : (tree.data.children ?? []).length === 0 ? (
            <EmptyState message={t("assets.noChildren")} />
          ) : (
            <div className="space-y-3">
              {health.isError ? (
                <ErrorState error={health.error} onRetry={() => void health.refetch()} />
              ) : health.isPending ? (
                <LoadingState />
              ) : null}
              <ul className="space-y-3">
                {(tree.data.children ?? []).map((child) => (
                  <ComponentTree
                    key={child.id}
                    node={child}
                    health={new Map((health.data ?? []).map((item) => [item.asset_id, item]))}
                  />
                ))}
              </ul>
            </div>
          )}
        </TabsContent>

        <TabsContent value="meters">
          {can("meters.reading.record") && (a.meters?.length ?? 0) > 0 ? (
            <div className="mb-3 flex justify-end">
              <Button size="sm" onClick={() => setDialog({ kind: "reading" })}>
                {t("assets.recordReading")}
              </Button>
            </div>
          ) : null}
          {!a.meters || a.meters.length === 0 ? (
            <EmptyState />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t("app.name")}</Th>
                  <Th>{t("app.type")}</Th>
                  <Th>{t("assets.readingValue")}</Th>
                  <Th>{t("app.updated")}</Th>
                  <Th>{t("assets.averageDailyUsage")}</Th>
                </tr>
              </thead>
              <tbody>
                {a.meters.map((m) => (
                  <Tr key={m.id}>
                    <Td>{m.name}</Td>
                    <Td className="text-muted-foreground">{m.type_key}</Td>
                    <Td className="tabular-nums">
                      {formatQuantity(m.current_value)} {m.unit}
                    </Td>
                    <Td>{formatDateTime(m.current_at)}</Td>
                    <Td className="tabular-nums">{formatQuantity(m.avg_daily_usage)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
          {can("meters.meter.view") ? (
            <div className="mt-4 min-w-0 space-y-6">
              {(a.meters ?? []).map((meter) => (
                <AssetMeterHistory key={meter.id} meter={meter} />
              ))}
            </div>
          ) : null}
        </TabsContent>

        <TabsContent value="workorders">
          {wos.isPending && wos.fetchStatus !== "idle" ? (
            <LoadingState />
          ) : !wos.data || wos.data.length === 0 ? (
            <EmptyState />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t("app.code")}</Th>
                  <Th>{t("app.title")}</Th>
                  <Th>{t("app.priority")}</Th>
                  <Th>{t("app.status")}</Th>
                  <Th>{t("app.due")}</Th>
                </tr>
              </thead>
              <tbody>
                {wos.data.map((w) => (
                  <Tr key={w.id}>
                    <Td>
                      <Link
                        to="/work-orders/$woId"
                        params={{ woId: w.id }}
                        className="font-mono text-xs hover:underline"
                      >
                        {w.code}
                      </Link>
                    </Td>
                    <Td>{w.title}</Td>
                    <Td>
                      <StatusBadge value={w.priority} namespace="workOrders.priority" />
                    </Td>
                    <Td>
                      <StatusBadge value={w.status} namespace="workOrders.status" />
                    </Td>
                    <Td className={w.is_overdue ? "text-destructive" : undefined}>
                      {formatDateTime(w.due_at)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </TabsContent>

        <TabsContent value="certificates">
          {can("assets.certificate.manage") ? (
            <div className="mb-2 flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDialog({ kind: "certificate" })}
              >
                {t("assets.addCertificate")}
              </Button>
            </div>
          ) : null}
          {!certs.data || certs.data.length === 0 ? (
            <EmptyState />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t("app.name")}</Th>
                  <Th>{t("app.type")}</Th>
                  <Th>{t("app.status")}</Th>
                  <Th>{t("assets.expires")}</Th>
                  {can("documents.document.view") ? (
                    <Th>{t("fileEvidence.certificateFile")}</Th>
                  ) : null}
                  {can("assets.certificate.manage") ? <Th /> : null}
                </tr>
              </thead>
              <tbody>
                {certs.data.map((c) => (
                  <Tr key={c.id}>
                    <Td>
                      {c.name}{" "}
                      {c.number ? (
                        <span className="text-muted-foreground">({c.number})</span>
                      ) : null}
                    </Td>
                    <Td className="text-muted-foreground">
                      {t(`assets.certificateKinds.${c.kind}`, { defaultValue: c.kind })}
                    </Td>
                    <Td>
                      <StatusBadge value={c.status} namespace="assets.certStatus" />
                    </Td>
                    <Td>
                      {formatDate(c.expires_on)}
                      {c.days_to_expiry !== null ? (
                        <span className="ms-1 text-xs text-muted-foreground">
                          ({c.days_to_expiry}d)
                        </span>
                      ) : null}
                    </Td>
                    {can("documents.document.view") ? (
                      <Td>
                        {c.document_id ? (
                          <DocumentFileLink id={c.document_id} versionId={c.document_version_id} />
                        ) : (
                          "—"
                        )}
                      </Td>
                    ) : null}
                    {can("assets.certificate.manage") ? (
                      <Td className="text-end">
                        {c.status !== "revoked" ? (
                          <div className="flex flex-wrap justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDialog({ kind: "certificate", certificate: c })}
                            >
                              {t("app.edit")}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDialog({ kind: "revoke", certificate: c })}
                            >
                              {t("feedbackAssets.revokeCertificate")}
                            </Button>
                          </div>
                        ) : null}
                      </Td>
                    ) : null}
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </TabsContent>
        <TabsContent value="history">
          <AssetMaintenanceHistory assetId={a.id} />
        </TabsContent>
        <TabsContent value="documents">
          <Attachments entityType="asset" entityId={a.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
